import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { generateAndCopy } from '../../utils/generatePassword';
import './ResetPassword.css';

/* ─── Icons ─────────────────────────────────────── */
function IconEye() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconEyeOff() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function IconDice() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="8.5" cy="15.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="15.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* ─── Main component ─────────────────────────────── */
function ResetPassword() {
  const navigate = useNavigate();

  // 'loading' | 'ready' | 'invalid' | 'success'
  const [status, setStatus] = useState('loading');

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [show, setShow] = useState({ password: false, confirm: false });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [clipboardMsg, setClipboardMsg] = useState('');

  async function handleGenerate() {
    const pw = await generateAndCopy();
    setForm({ password: pw, confirm: pw });
    setShow({ password: true, confirm: true });
    setErrors({});
    setClipboardMsg('Contraseña generada y copiada al portapapeles');
    setTimeout(() => setClipboardMsg(''), 3000);
  }

  useEffect(() => {
    // Supabase JS v2 auto-parses the recovery token from the URL hash.
    // We check for an active session, then listen for PASSWORD_RECOVERY.
    let resolved = false;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!resolved) {
        resolved = true;
        setStatus(session ? 'ready' : 'invalid');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        resolved = true;
        setStatus('ready');
      }
    });

    // If nothing resolves in 4 seconds, show invalid
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        setStatus('invalid');
      }
    }, 4000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  function validate() {
    const e = {};
    if (!form.password) {
      e.password = 'La nueva contraseña es requerida.';
    } else if (form.password.length < 8) {
      e.password = 'La contraseña debe tener al menos 8 caracteres.';
    }
    if (!form.confirm) {
      e.confirm = 'Confirma tu nueva contraseña.';
    } else if (form.confirm !== form.password) {
      e.confirm = 'Las contraseñas no coinciden.';
    }
    return e;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (submitError) setSubmitError('');
  }

  function toggleShow(field) {
    setShow((prev) => ({ ...prev, [field]: !prev[field] }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password: form.password });
    setSubmitting(false);

    if (error) {
      setSubmitError(
        error.message?.toLowerCase().includes('same password')
          ? 'La nueva contraseña debe ser diferente a la anterior.'
          : 'No se pudo actualizar la contraseña. El enlace puede haber expirado.'
      );
      return;
    }

    setStatus('success');
    setTimeout(async () => {
      await supabase.auth.signOut();
      navigate('/');
    }, 3500);
  }

  /* ── Loading ── */
  if (status === 'loading') {
    return (
      <div className="rp-container">
        <div className="rp-card rp-card--center">
          <div className="rp-spinner-large" aria-label="Cargando" />
          <p className="rp-loading-text">Verificando enlace…</p>
        </div>
      </div>
    );
  }

  /* ── Invalid / expired ── */
  if (status === 'invalid') {
    return (
      <div className="rp-container">
        <div className="rp-card rp-card--center">
          <div className="rp-state-icon rp-state-icon--error" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1 className="rp-heading">Enlace inválido</h1>
          <p className="rp-subheading">
            Este enlace ha expirado o ya fue utilizado. Solicita uno nuevo desde la pantalla de inicio de sesión.
          </p>
          <button className="rp-btn-primary" onClick={() => navigate('/')}>
            Ir al inicio de sesión
          </button>
        </div>
      </div>
    );
  }

  /* ── Success ── */
  if (status === 'success') {
    return (
      <div className="rp-container">
        <div className="rp-card rp-card--center">
          <div className="rp-state-icon rp-state-icon--success" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h1 className="rp-heading">¡Contraseña actualizada!</h1>
          <p className="rp-subheading">
            Tu contraseña ha sido cambiada exitosamente. Serás redirigido al inicio de sesión en unos segundos.
          </p>
          <div className="rp-redirect-bar" aria-hidden="true" />
        </div>
      </div>
    );
  }

  /* ── Form ── */
  return (
    <div className="rp-container">
      <div className="rp-card">

        {/* Logo */}
        <div className="rp-logo">
          <img src="/WSP_LOGO-01.jpg" alt="WSP" className="rp-logo-img" />
          <span className="rp-logo-text">T.R.A.C.K.</span>
          <p className="rp-logo-subtitle">Time &amp; Requirements ABAP Control Kardex</p>
        </div>

        {/* Header */}
        <div className="rp-header">
          <div className="rp-header-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h1 className="rp-heading">Nueva contraseña</h1>
          <p className="rp-subheading">Elige una contraseña segura para tu cuenta.</p>
        </div>

        {/* Form */}
        <form className="rp-form" onSubmit={handleSubmit} noValidate>

          {/* New password */}
          <div className="rp-form-group">
            <label htmlFor="rp-password" className="rp-label">Nueva contraseña</label>
            <div className={`rp-input-wrapper${errors.password ? ' rp-input-error' : ''}`}>
              <input
                id="rp-password"
                type={show.password ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="rp-generate-btn"
                onClick={handleGenerate}
                aria-label="Generar contraseña segura"
                title="Generar contraseña segura"
              >
                <IconDice />
              </button>
              <button
                type="button"
                className="rp-toggle-btn"
                onClick={() => toggleShow('password')}
                aria-label={show.password ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {show.password ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
            {errors.password && <span className="rp-error-msg">{errors.password}</span>}
            {clipboardMsg && <span className="rp-clipboard-msg">{clipboardMsg}</span>}
          </div>

          {/* Confirm password */}
          <div className="rp-form-group">
            <label htmlFor="rp-confirm" className="rp-label">Confirmar contraseña</label>
            <div className={`rp-input-wrapper${errors.confirm ? ' rp-input-error' : ''}`}>
              <input
                id="rp-confirm"
                type={show.confirm ? 'text' : 'password'}
                name="confirm"
                value={form.confirm}
                onChange={handleChange}
                placeholder="••••••••"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="rp-toggle-btn"
                onClick={() => toggleShow('confirm')}
                aria-label={show.confirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {show.confirm ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
            {errors.confirm && <span className="rp-error-msg">{errors.confirm}</span>}
          </div>

          {submitError && (
            <div className="rp-error-banner" role="alert">{submitError}</div>
          )}

          <button type="submit" className="rp-btn-primary" disabled={submitting}>
            {submitting ? (
              <>
                <span className="rp-spinner" aria-hidden="true" />
                Actualizando…
              </>
            ) : (
              'Actualizar contraseña'
            )}
          </button>

        </form>

      </div>
    </div>
  );
}

export default ResetPassword;
